import { createContext, useContext, useState, useCallback } from 'react';

// ── Consent Categories ────────────────────────────────────────────────────────
//
//  necessary   — always on; session auth + theme preference
//  analytics   — Sentry session replays + performance monitoring
//  preferences — feed tab, editor tour, font size, other UX memory
//
// ─────────────────────────────────────────────────────────────────────────────

const CONSENT_KEY     = 'chronos_consent';
const CONSENT_VERSION = '1.0';

const DEFAULT_CONSENT = {
  version:     CONSENT_VERSION,
  timestamp:   null,
  necessary:   true,
  analytics:   false,
  preferences: false,
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeStored = (consent) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
};

const ConsentContext = createContext({
  consent:       DEFAULT_CONSENT,
  hasConsented:  false,
  updateConsent: () => {},
  resetConsent:  () => {},
});

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => {
    const stored = readStored();
    if (!stored) return DEFAULT_CONSENT;
    // Stale version — will prompt again via hasConsented = false
    if (stored.version !== CONSENT_VERSION) return DEFAULT_CONSENT;
    return stored;
  });

  const hasConsented = !!readStored()?.version && readStored()?.version === CONSENT_VERSION;

  const updateConsent = useCallback((updates) => {
    const next = {
      ...DEFAULT_CONSENT,
      ...updates,
      necessary:  true,    // always on
      version:    CONSENT_VERSION,
      timestamp:  new Date().toISOString(),
    };
    setConsent(next);
    writeStored(next);
    return next;
  }, []);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsent(DEFAULT_CONSENT);
  }, []);

  return (
    <ConsentContext.Provider value={{ consent, hasConsented, updateConsent, resetConsent }}>
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = () => useContext(ConsentContext);
